// Stok kartı (yeni / düzenle). Durum ve kayıt mantığı useStockCardForm'da; bu dosya yalnız yerleşimi kurar:
// üstte kimlik şeridi, solda bölümler, sağda canlı özet, altta sabit kaydet çubuğu.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton,
  Paper, Snackbar, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import InventoryCategoryModal from './InventoryCategoryModal';
import InventoryBrandModal from './InventoryBrandModal';
import InventoryWarehouseModal from './InventoryWarehouseModal';
import ImageLibrarySelectionModal from './ImageLibrarySelectionModal';
import GuideButton from '../../common/guide/GuideButton';
import { useStockCardForm } from './stockCard/useStockCardForm';
import type { AddInventoryItemProps } from './stockCard/types';
import { IdentitySection } from './stockCard/sections/IdentitySection';
import { GeneralSection } from './stockCard/sections/GeneralSection';
import { PriceTaxSection } from './stockCard/sections/PriceTaxSection';
import { StockSection } from './stockCard/sections/StockSection';
import { PackagingSection } from './stockCard/sections/PackagingSection';
import { ProductionLotSection } from './stockCard/sections/ProductionLotSection';
import { FeaturesSection } from './stockCard/sections/FeaturesSection';
import { OtherSection } from './stockCard/sections/OtherSection';
import { SummaryPanel, type SummaryProblem } from './stockCard/sections/SummaryPanel';

export type { AddInventoryItemProps, StockCardPrefill } from './stockCard/types';

type SectionKey = 'general' | 'price' | 'stock' | 'packaging' | 'production' | 'features' | 'other';

const AddInventoryItem: React.FC<AddInventoryItemProps> = (props) => {
  const f = useStockCardForm(props);
  const { t } = useTranslation('accounting');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));
  const [active, setActive] = useState<SectionKey>('general');

  useEffect(() => { if (f.open) setActive('general'); }, [f.open]);

  const isProduct = f.itemType === 'PRODUCT';
  const sections = useMemo(() => ([
    { key: 'general' as const, label: t('stock.addModal.layout.sections.general', 'Genel'), visible: true },
    { key: 'price' as const, label: t('stock.addModal.layout.sections.price', 'Fiyat ve vergi'), visible: true },
    { key: 'stock' as const, label: t('stock.addModal.layout.sections.stock', 'Stok ve depo'), visible: isProduct },
    { key: 'packaging' as const, label: t('stock.addModal.layout.sections.packaging', 'Ambalaj ve barkodlar'), visible: isProduct },
    {
      key: 'production' as const,
      label: t('stock.addModal.layout.sections.production', 'Üretim ve lot'),
      visible: isProduct && (f.productionEnabled || f.lotTrackingEnabled),
    },
    { key: 'features' as const, label: t('stock.addModal.layout.sections.features', 'Özellikler ve varyant'), visible: true },
    { key: 'other' as const, label: t('stock.addModal.layout.sections.other', 'Diğer'), visible: true },
  ]).filter(s => s.visible), [t, isProduct, f.productionEnabled, f.lotTrackingEnabled]);

  // Gizlenen bölümde kalındıysa Genel'e dön (ör. hizmete geçildi).
  useEffect(() => {
    if (!sections.some(s => s.key === active)) setActive('general');
  }, [sections, active]);

  const qty = f.quantity === '' ? 0 : Number(f.quantity);
  const saleMissing = typeof f.salePrice === 'string' ? f.salePrice === '' : f.salePrice === 0;
  const problems: SummaryProblem[] = [];
  if (!f.itemName.trim()) problems.push({ section: 'identity', message: t('stock.addModal.layout.missingName', 'Ad girilmedi') });
  if (f.vatRate === '') problems.push({ section: 'price', message: t('stock.addModal.layout.missingVat', 'KDV oranı girilmedi') });
  if (f.vatStatus === '') problems.push({ section: 'price', message: t('stock.addModal.layout.missingVatStatus', 'KDV dahil/hariç seçilmedi') });
  if (saleMissing) problems.push({ section: 'price', message: t('stock.addModal.layout.missingSale', 'Satış fiyatı girilmedi') });
  if (isProduct && !f.isEditMode && qty > 0 && !f.warehouseId) {
    problems.push({ section: 'stock', message: t('stock.addModal.layout.missingWarehouse', 'Açılış stoğu için depo seçilmedi') });
  }
  if (isProduct && !f.isEditMode && qty > 0 && f.isSmartWh && !f.selectedLocationId) {
    problems.push({ section: 'stock', message: t('stock.addModal.layout.missingLocation', 'Akıllı depoda lokasyon seçilmedi') });
  }
  if (f.barcodeExists) problems.push({ section: 'identity', message: t('stock.addModal.layout.barcodeTaken', 'Bu barkod başka bir üründe kayıtlı') });
  if (f.barcodePackageOwner) problems.push({ section: 'identity', message: f.barcodePackageOwner });

  const sectionHasProblem = (key: string) => problems.some(p => p.section === key);

  const goTo = (key: string) => {
    if (key === 'identity') {
      document.getElementById('stock-card-name')?.focus();
      return;
    }
    if (sections.some(s => s.key === key)) setActive(key as SectionKey);
  };

  const onSave = (keepOpenForNext: boolean) => {
    if (problems.length > 0) {
      f.setError(problems.map(p => p.message).join(' · '));
      goTo(problems[0].section);
      return;
    }
    f.setError(null);
    f.handleSave(keepOpenForNext);
  };

  const pane = (key: SectionKey, node: React.ReactNode) => (
    // Tüm bölümler bağlı kalır (birim düzenleyicisinin kayıt ref'i ve alan durumları için); yalnız gizlenir.
    <Box key={key} role="tabpanel" id={`stock-card-pane-${key}`} hidden={active !== key} sx={{ minWidth: 0 }}>
      {node}
    </Box>
  );

  const summary = <SummaryPanel f={f} problems={problems} onGoTo={goTo} />;

  return (
    <>
      <Dialog
        open={f.open}
        onClose={f.handleClose}
        fullScreen={!isDesktop}
        maxWidth={false}
        PaperProps={{
          sx: isDesktop
            ? { width: 'min(1240px, 96vw)', height: 'min(860px, 94vh)', borderRadius: 2, display: 'flex', flexDirection: 'column' }
            : { display: 'flex', flexDirection: 'column' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
            {f.isEditMode ? t('stock.addModal.titleEdit') : t('stock.addModal.titleNew')}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={f.itemType}
            onChange={(_, value) => { if (value) f.setItemType(value); }}
            disabled={f.isEditMode}
            aria-label={t('stock.addModal.layout.cardType', 'Kart tipi')}
          >
            <ToggleButton value="PRODUCT" sx={{ px: 2 }}>{t('stock.addModal.itemType.product')}</ToggleButton>
            <ToggleButton value="SERVICE" sx={{ px: 2 }}>{t('stock.addModal.itemType.service')}</ToggleButton>
          </ToggleButtonGroup>
          <GuideButton guideKey="inventory.add" />
          <IconButton edge="end" onClick={f.handleClose} aria-label={t('stock.common.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {f.initialLoading ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <Box sx={{ px: 2.5, py: 2, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
              <IdentitySection f={f} />
            </Box>

            {!isDesktop && (
              <Tabs
                value={active}
                onChange={(_, value) => setActive(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40 }}
              >
                {sections.map(s => (
                  <Tab
                    key={s.key}
                    value={s.key}
                    sx={{ minHeight: 40, textTransform: 'none' }}
                    label={sectionHasProblem(s.key) ? `${s.label} ●` : s.label}
                  />
                ))}
              </Tabs>
            )}

            <DialogContent sx={{ p: 0, flex: 1, display: 'flex', minHeight: 0 }}>
              {isDesktop && (
                <Box
                  component="nav"
                  aria-label={t('stock.addModal.layout.sectionsNav', 'Kart bölümleri')}
                  sx={{ width: 220, flex: 'none', borderRight: '1px solid', borderColor: 'divider', py: 1.5, px: 1, overflowY: 'auto' }}
                >
                  <Box role="tablist" aria-orientation="vertical" sx={{ display: 'grid', gap: 0.25 }}>
                    {sections.map(s => {
                      const selected = s.key === active;
                      const problem = sectionHasProblem(s.key);
                      return (
                        <Box
                          key={s.key}
                          component="button"
                          type="button"
                          role="tab"
                          aria-selected={selected}
                          aria-controls={`stock-card-pane-${s.key}`}
                          onClick={() => setActive(s.key)}
                          sx={{
                            all: 'unset',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            fontSize: '0.9rem',
                            // Kalınlık sabit: seçilince yazı genişleyip alt satıra kaymasın.
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: selected ? 'primary.main' : 'text.primary',
                            bgcolor: selected ? 'action.selected' : 'transparent',
                            '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                          }}
                        >
                          <span>{s.label}</span>
                          {problem && (
                            <Box
                              component="span"
                              title={t('stock.addModal.layout.sectionHasMissing', 'Eksik zorunlu alan var')}
                              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flex: 'none' }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2.5 }}>
                {f.error && f.error !== 'Bu barkod zaten sistemde kayıtlı' && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => f.setError(null)}>{f.error}</Alert>
                )}
                {pane('general', <GeneralSection f={f} />)}
                {pane('price', (
                  <>
                    <PriceTaxSection f={f} />
                    {!isWide && (
                      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>{summary}</Paper>
                    )}
                  </>
                ))}
                {isProduct && pane('stock', <StockSection f={f} />)}
                {isProduct && pane('packaging', <PackagingSection f={f} />)}
                {isProduct && (f.productionEnabled || f.lotTrackingEnabled) && pane('production', <ProductionLotSection f={f} />)}
                {pane('features', <FeaturesSection f={f} />)}
                {pane('other', <OtherSection f={f} />)}
              </Box>

              {isWide && (
                <Box
                  component="aside"
                  aria-label={t('stock.addModal.layout.summary', 'Canlı özet')}
                  sx={{ width: 260, flex: 'none', borderLeft: '1px solid', borderColor: 'divider', p: 2, overflowY: 'auto', bgcolor: 'action.hover' }}
                >
                  {summary}
                </Box>
              )}
            </DialogContent>
          </>
        )}

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1,
            flexWrap: 'wrap',
            pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', md: 1.5 },
          }}
        >
          <Typography
            variant="body2"
            color={problems.length > 0 ? 'error' : 'text.secondary'}
            sx={{ flex: 1, minWidth: 160 }}
          >
            {problems.length > 0
              ? t('stock.addModal.layout.missingCount', '{{count}} zorunlu alan eksik', { count: problems.length })
              : ''}
          </Typography>
          <Button onClick={f.handleClose} variant="outlined" size="small">{t('stock.common.cancel')}</Button>
          {!f.isEditMode && (
            <Button onClick={() => onSave(true)} variant="outlined" size="small" disabled={f.loading}>
              {t('stock.common.saveAndNew', 'Kaydet ve Yeni Ekle')}
            </Button>
          )}
          <Button onClick={() => onSave(false)} variant="contained" size="small" disabled={f.loading} sx={{ minWidth: 110 }}>
            {f.loading ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                {f.isEditMode ? t('stock.common.updating') : t('stock.common.saving')}
              </>
            ) : (
              f.isEditMode ? t('stock.common.update') : t('stock.common.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Kaydet ve Yeni Ekle" başarı bildirimi */}
      <Snackbar
        open={!!f.successMsg}
        autoHideDuration={3000}
        onClose={() => f.setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (th) => th.zIndex.modal + 10 }}
      >
        <Alert onClose={() => f.setSuccessMsg(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {f.successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!f.unitNotice}
        autoHideDuration={6000}
        onClose={() => f.setUnitNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (th) => th.zIndex.modal + 10 }}
      >
        <Alert onClose={() => f.setUnitNotice(null)} severity={f.unitNotice?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {f.unitNotice?.message}
        </Alert>
      </Snackbar>

      {/* Barkod çakışması */}
      <Dialog
        open={f.isDuplicateBarcodeModalOpen}
        onClose={f.handleCloseDuplicateBarcodeModal}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorOutlineIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Barkod Çakışması</Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={f.handleCloseDuplicateBarcodeModal} aria-label="kapat">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          {f.existingItem && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>"{f.barcode}"</strong> barkodlu ürün zaten sistemde kayıtlı. Aynı barkoda sahip farklı bir ürün ekleyemezsiniz.
              </Typography>
              <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Mevcut Ürün Bilgileri:</Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Ürün Adı:</Typography>
                    <Typography variant="body1" fontWeight="medium">{f.existingItem.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Stok Kodu:</Typography>
                    <Typography variant="body1" fontWeight="medium">{f.existingItem.stockCode}</Typography>
                  </Grid>
                  {f.existingItem.categoryId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Kategori:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {f.categories.find(c => c.id === f.existingItem?.categoryId)?.name || 'Bilinmiyor'}
                      </Typography>
                    </Grid>
                  )}
                  {f.existingItem.warehouseId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Depo:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {f.warehouses.find(w => w.id === f.existingItem?.warehouseId)?.name || 'Bilinmiyor'}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Miktar:</Typography>
                    <Typography variant="body1" fontWeight="medium">{f.existingItem.quantity} {f.existingItem.unit}</Typography>
                  </Grid>
                </Grid>
              </Paper>
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                <Typography variant="body2">Lütfen farklı bir barkod girin veya başka bir ürün seçin.</Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={f.handleCloseDuplicateBarcodeModal} variant="contained" color="primary" size="medium" fullWidth>
            Tamam
          </Button>
        </DialogActions>
      </Dialog>

      <InventoryCategoryModal open={f.isCategoryModalOpen} onClose={f.handleCloseCategoryModal} branchId={f.branchesId ? f.branchesId : undefined} />
      <InventoryBrandModal open={f.isBrandModalOpen} onClose={f.handleCloseBrandModal} branchId={f.branchesId ? f.branchesId : undefined} />
      <InventoryWarehouseModal open={f.isWarehouseModalOpen} onClose={f.handleCloseWarehouseModal} branchId={f.branchesId ? f.branchesId : undefined} />
      <ImageLibrarySelectionModal
        open={f.isImageLibraryModalOpen}
        onClose={() => f.setIsImageLibraryModalOpen(false)}
        onSave={(selectedUrls) => {
          f.setImageUrls(selectedUrls);
          f.setIsImageLibraryModalOpen(false);
        }}
        productId={f.itemToEdit?.id || null}
        productName={f.itemName || ''}
        currentImageUrls={f.imageUrls}
        companyId={f.companyId}
      />
    </>
  );
};

export default AddInventoryItem;
